import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaHistory } from 'react-icons/fa';
import { Modal } from './Modals';

// Generic viewer for services/masterRevisions.js history (API Master, Product
// Master, Equipment Master, Equipment Train, Acceptance Criteria today — any
// module that calls recordRevision() gets this for free). Each revision is a
// full JSON snapshot of the row at the moment it was approved; this renders
// what changed since the previous revision, who made it, and when, instead of
// the raw snapshot the backend returns.

// Fields that are noisy or redundant in a diff (internal bookkeeping, or
// already shown elsewhere in the row header) — hidden from the changed-fields list.
const DIFF_SKIP = new Set([
  'updated_at', 'created_at', 'updated_by', 'created_by', 'attached_files',
  'approval_decision_by', 'approval_decision_at', 'qa_approved_by', 'qa_approved_at',
  'product_approved_by', 'product_approved_at',
]);

const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 120);
  return String(v);
};

const diffSnapshots = (current, previous) => {
  if (!previous) return null; // first revision — nothing to diff against
  const keys = new Set([...Object.keys(current || {}), ...Object.keys(previous || {})]);
  const changes = [];
  keys.forEach((key) => {
    if (DIFF_SKIP.has(key)) return;
    const before = previous?.[key];
    const after = current?.[key];
    if (JSON.stringify(before) === JSON.stringify(after)) return;
    changes.push({ key, before, after });
  });
  return changes;
};

const RevisionRow = ({ revision, previous, isLatest }) => {
  const [expanded, setExpanded] = useState(false);
  const changes = diffSnapshots(revision.snapshot, previous?.snapshot);
  const who = revision.created_by_user || 'Unknown';
  const when = revision.created_at ? new Date(revision.created_at).toLocaleString() : '—';
  const why = revision.snapshot?.approval_decision_comment || revision.snapshot?.qa_approval_comment
    || revision.snapshot?.product_approval_comment || null;

  return (
    <div className="card" style={{ marginBottom: '10px' }}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((v) => !v); } }}
      >
        <div>
          <strong>Revision {revision.revision_number}</strong>{isLatest && <span className="tag" style={{ marginLeft: '8px', background: 'var(--success-bg)', color: 'var(--success-text)' }}>Current</span>}
          <div className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>By {who} on {when}</div>
        </div>
        <span className="text-muted">{expanded ? 'Hide' : 'Show'} details</span>
      </div>
      {expanded && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--border-color)' }}>
          {why && <p style={{ margin: '10px 0 6px' }}><strong>Comment: </strong>{why}</p>}
          {changes === null ? (
            <p className="text-muted" style={{ margin: '10px 0' }}>First recorded revision — no prior version to compare against.</p>
          ) : changes.length === 0 ? (
            <p className="text-muted" style={{ margin: '10px 0' }}>No tracked field changed since the previous revision (status/approval metadata only).</p>
          ) : (
            <table style={{ marginTop: '10px' }}>
              <thead><tr><th>Field</th><th>Previous</th><th>New</th></tr></thead>
              <tbody>
                {changes.map((c) => (
                  <tr key={c.key}>
                    <td>{c.key}</td>
                    <td className="text-muted">{formatValue(c.before)}</td>
                    <td>{formatValue(c.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const VersionHistoryModal = ({ isOpen, onClose, title, fetcher }) => {
  const [loading, setLoading] = useState(true);
  const [revisions, setRevisions] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    setLoading(true);
    fetcher()
      .then((res) => { if (alive) setRevisions(res?.data?.data || []); })
      .catch((err) => { toast.error(err?.response?.data?.message || 'Failed to load version history'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isOpen, fetcher]);

  if (!isOpen) return null;

  return (
    <Modal isOpen onClose={onClose} title={title || 'Version History'} size="lg" footer={<button className="btn btn-outline" onClick={onClose}>Close</button>}>
      {loading ? (
        <p className="text-muted">Loading...</p>
      ) : revisions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-text-secondary">
          <FaHistory className="text-3xl mb-2" />
          <span className="text-sm">No revision has been recorded yet — a revision is captured each time this record is approved.</span>
        </div>
      ) : (
        <>
          <p className="text-muted" style={{ marginBottom: '12px' }}>
            Newest first. Each revision is the full record as it stood the moment it was approved — a later edit never changes what an earlier study or report was actually based on.
          </p>
          {revisions.map((rev, idx) => (
            <RevisionRow key={rev.id} revision={rev} previous={revisions[idx + 1]} isLatest={idx === 0} />
          ))}
        </>
      )}
    </Modal>
  );
};

export default VersionHistoryModal;
